import {
	Badge,
	Box,
	CircularProgress,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../hooks/UserContext";
import { ISSUE_COUNT_PATHS, useIssueCounts } from "../../hooks/IssueCountsContext";
import { useAirtableStatus } from "../../hooks/useAirtableStatus";
import { AppVersion } from "../helpers";
import { API } from "@infra/implementations/api";
import { User } from "../../models";
import { setJwtToken, setRefreshToken } from "../../utilities/utils";

import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import FeedbackIcon from "@mui/icons-material/Feedback";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { NotInterested } from "@mui/icons-material";
import * as Sentry from "@sentry/react";

import "./DrawerNavigation.css";
import { type NavLink, USER_LINKS, LOGIN_LINK, ADMIN_LINKS, DEVELOPER_LINKS } from "./navLinks";
export type { NavLink } from "./navLinks";

export const SIDEBAR_COLLAPSED_WIDTH = 60;
const MORE_BUTTON_HEIGHT = 48;

type TopItem =
	| { kind: "divider"; key: string }
	| { kind: "nav"; key: string; link: NavLink }
	| { kind: "logout"; key: string };

const ItemBreak = React.memo(function ItemBreak() {
	return <ListItem className="sidebar-item-break" />;
});

const STATUS_COLORS: Record<string, string> = {
	none: "#4caf50",
	minor: "#ff9800",
	major: "#f44336",
	critical: "#f44336",
};

function AirtableStatusIndicator({ expanded }: { expanded: boolean }) {
	const { indicator } = useAirtableStatus();
	const color = indicator ? STATUS_COLORS[indicator] : "#9e9e9e";

	return (
		<Tooltip title={expanded ? "" : "Airtable"} placement="right">
			<ListItemButton onClick={() => window.open("https://status.airtable.com", "_blank", "noopener,noreferrer")}>
				<ListItemIcon>
					<Box className="sidebar-status-icon">
						<Box className="sidebar-status-dot" sx={{ backgroundColor: color }} />
					</Box>
				</ListItemIcon>
				<ListItemText primary="Airtable Status" />
			</ListItemButton>
		</Tooltip>
	);
}

function logout() {
	setJwtToken("");
	setRefreshToken("");
	localStorage.clear();
	sessionStorage.clear();
	window.location.href = "/login";
}

interface DrawerNavigationProps {
	onNavigate: (link: NavLink) => void;
	mobileOpen: boolean;
	onMobileClose: () => void;
}

export const DrawerNavigation = (props: DrawerNavigationProps) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const user = React.useContext(UserContext) as User | undefined;
	const { counts, loading: issueCountsLoading } = useIssueCounts();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [expanded, setExpanded] = React.useState(false);
	const [moreAnchor, setMoreAnchor] = React.useState<HTMLElement | null>(null);
	const [overflowIndex, setOverflowIndex] = React.useState<number | null>(null);

	const contentRef = React.useRef<HTMLDivElement>(null);
	const ghostRef = React.useRef<HTMLUListElement>(null);
	const bottomRef = React.useRef<HTMLDivElement>(null);

	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	const handleNavClick = (link: NavLink) => {
		props.onNavigate(link);
		navigate(buildNavPath(link.to), { state: { title: link.text } });
		if (isMobile) props.onMobileClose();
		setMoreAnchor(null);
		setExpanded(false);
	};

	const handleLogout = () => {
		logout();
		if (isMobile) props.onMobileClose();
		setMoreAnchor(null);
		setExpanded(false);
	};

	const topItems: TopItem[] = React.useMemo(() => {
		const items: TopItem[] = [];
		const userIsLoggedIn = !!user?.id;
		const links = userIsLoggedIn ? USER_LINKS : [LOGIN_LINK];
		links.forEach((link) => items.push({ kind: "nav", key: link.to, link }));
		if (userIsLoggedIn) items.push({ kind: "logout", key: "logout" });
		if (user?.isAdmin) {
			items.push({ kind: "divider", key: "div-admin" });
			ADMIN_LINKS.forEach((link) => items.push({ kind: "nav", key: link.to, link }));
		}
		if (user?.isDeveloper) {
			items.push({ kind: "divider", key: "div-dev" });
			DEVELOPER_LINKS.forEach((link) => items.push({ kind: "nav", key: link.to, link }));
		}
		return items;
	}, [user?.id, user?.isAdmin, user?.isDeveloper]);

	React.useLayoutEffect(() => {
		if (isMobile) {
			setOverflowIndex(null);
			return;
		}
		const paperEl = contentRef.current?.closest(".MuiDrawer-paper") as HTMLElement | null;
		const ghost = ghostRef.current;
		const bottom = bottomRef.current;
		if (!paperEl || !ghost || !bottom) return;

		const compute = () => {
			const available = paperEl.clientHeight - bottom.offsetHeight;
			if (available <= 0) return;

			if (ghost.scrollHeight <= available) {
				setOverflowIndex(null);
				return;
			}

			const budget = available - MORE_BUTTON_HEIGHT;
			const children = Array.from(ghost.children) as HTMLElement[];
			const ghostTop = ghost.offsetTop;
			let cutoff = children.length;
			for (let i = 0; i < children.length; i++) {
				const child = children[i];
				const bottomEdge = child.offsetTop + child.offsetHeight - ghostTop;
				if (bottomEdge > budget) {
					cutoff = i;
					break;
				}
			}
			while (cutoff > 0 && topItems[cutoff - 1]?.kind === "divider") cutoff--;
			setOverflowIndex(cutoff);
		};

		compute();
		const ro = new ResizeObserver(compute);
		ro.observe(paperEl);
		ro.observe(bottom);
		return () => ro.disconnect();
	}, [isMobile, topItems]);

	const showExpanded = isMobile || expanded;
	const drawerClass = `sidebar-drawer${showExpanded ? " expanded" : ""}`;

	const renderItem = (item: TopItem, inMenu: boolean): React.ReactNode => {
		if (item.kind === "divider") {
			return inMenu ? null : <ItemBreak key={item.key} />;
		}

		const onClick = item.kind === "nav" ? () => handleNavClick(item.link) : handleLogout;
		const rawIcon = item.kind === "nav" ? item.link.icon : <LogoutIcon />;
		const label = item.kind === "nav" ? item.link.text : "Logout";

		// Decorate the icon with an issue-count badge for any nav row whose path has a fetcher
		// in IssueCountsContext. Badge shows a CircularProgress while the initial fetch is in
		// flight (count still undefined), the number when > 0, and is hidden when the count is
		// known to be 0 — Badge with badgeContent={0} self-hides via showZero defaulting to false.
		const navPath = item.kind === "nav" ? item.link.to : undefined;
		const hasFetcher = navPath ? ISSUE_COUNT_PATHS.has(navPath) : false;
		const count = navPath ? counts[navPath] : undefined;
		// Three states for a count-fetcher path:
		//   - in flight: <CircularProgress> as badgeContent
		//   - resolved > 0: numeric count
		//   - resolved 0: omit the Badge wrapper entirely so no badge node lingers in the DOM
		const isFetching = hasFetcher && count === undefined && issueCountsLoading;
		const showCountBadge = hasFetcher && count !== undefined && count > 0;
		const icon = isFetching ? (
			<Badge
				badgeContent={<CircularProgress size={10} thickness={6} sx={{ color: "inherit" }} />}
				color="error"
				overlap="circular"
			>
				{rawIcon}
			</Badge>
		) : showCountBadge ? (
			<Badge badgeContent={count} color="error" overlap="circular">
				{rawIcon}
			</Badge>
		) : (
			rawIcon
		);

		if (inMenu) {
			return (
				<MenuItem key={item.key} onClick={onClick}>
					<ListItemIcon>{icon}</ListItemIcon>
					<ListItemText primary={label} />
				</MenuItem>
			);
		}

		return (
			<ListItem key={item.key} disablePadding>
				<Tooltip title={showExpanded ? "" : label} placement="right">
					<ListItemButton onClick={onClick}>
						<ListItemIcon>{icon}</ListItemIcon>
						<ListItemText primary={label} />
					</ListItemButton>
				</Tooltip>
			</ListItem>
		);
	};

	const visibleTopItems = overflowIndex === null ? topItems : topItems.slice(0, overflowIndex);
	const overflowedItems = overflowIndex === null ? [] : topItems.slice(overflowIndex);

	const navList = (
		<Box ref={contentRef} className="sidebar-content">
			<List className="sidebar-top-list">
				{visibleTopItems.map((item) => renderItem(item, false))}
				{overflowedItems.length > 0 && (
					<ListItem key="more" disablePadding>
						<Tooltip title={showExpanded ? "" : "More"} placement="right">
							<ListItemButton onClick={(e) => setMoreAnchor(e.currentTarget)}>
								<ListItemIcon>
									<MoreHorizIcon />
								</ListItemIcon>
								<ListItemText primary="More" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
				)}
			</List>
			{!isMobile && (
				<List ref={ghostRef} className="sidebar-ghost-list" aria-hidden>
					{topItems.map((item) => renderItem(item, false))}
				</List>
			)}
			<Box ref={bottomRef} className="sidebar-bottom-block">
				<List>
					{user?.isAdmin && (
						<>
							<ListItem key="clear-cache" disablePadding>
								<Tooltip title={showExpanded ? "" : "Clear Cache"} placement="right">
									<ListItemButton
										onClick={() => {
											API.Data.clearCache();
											if (isMobile) props.onMobileClose();
										}}
									>
										<ListItemIcon>
											<NotInterested />
										</ListItemIcon>
										<ListItemText primary="Clear Portal Cache" />
									</ListItemButton>
								</Tooltip>
							</ListItem>
							<ListItem key="airtable-status" disablePadding>
								<AirtableStatusIndicator expanded={showExpanded} />
							</ListItem>
						</>
					)}
					<ListItem key="feedback" disablePadding>
						<Tooltip title={showExpanded ? "" : "Feedback"} placement="right">
							<ListItemButton
								onClick={async () => {
									const feedback = Sentry.getFeedback();
									if (feedback) {
										const form = await feedback.createForm();
										form.appendToDom();
										form.open();
									}
									if (isMobile) props.onMobileClose();
								}}
							>
								<ListItemIcon>
									<FeedbackIcon />
								</ListItemIcon>
								<ListItemText primary="Feedback" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
					<ListItem key="version" className="sidebar-version">
						<ListItemIcon>
							<InfoIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={<AppVersion />} />
					</ListItem>
				</List>
			</Box>
			<Menu
				anchorEl={moreAnchor}
				open={Boolean(moreAnchor)}
				onClose={() => setMoreAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{overflowedItems.map((item) => renderItem(item, true))}
			</Menu>
		</Box>
	);

	if (isMobile) {
		return (
			<Drawer
				variant="temporary"
				anchor="left"
				open={props.mobileOpen}
				onClose={props.onMobileClose}
				className="sidebar-drawer expanded sidebar-drawer-mobile"
			>
				{navList}
			</Drawer>
		);
	}

	return (
		<Drawer
			variant="permanent"
			anchor="left"
			className={drawerClass}
			onMouseEnter={() => setExpanded(true)}
			onMouseLeave={() => setExpanded(false)}
		>
			{navList}
		</Drawer>
	);
};
